import { SalesforceMarketingCloudService } from "@app/services/salesforce-marketing-cloud.service";
import { User } from "@app/models/user";
import { sfLog } from "@app/core/salesforce/salesforce-debug.logger";

/**
 * Helper para integração do Salesforce Marketing Cloud com o sistema de autenticação
 */

/**
 * Sincroniza os dados do usuário com o Salesforce após login
 *
 * @param user - Objeto User com os dados do cliente
 * @param salesforceService - Instância do serviço Salesforce
 */
export async function syncUserToSalesforce(
  user: User,
  salesforceService: SalesforceMarketingCloudService
): Promise<boolean> {
  try {
    const contactKey = user.clientId ? String(user.clientId).trim() : "";
    if (!contactKey) {
      sfLog.warn("syncUserToSalesforce abortado: user sem clientId (PV)");
      return false;
    }

    sfLog.info("syncUserToSalesforce: definindo contact key = PV", { contactKey });

    // 1. Definir Contact Key (PV)
    const contactKeySet = await salesforceService.setContactKey(contactKey);
    if (!contactKeySet) {
      sfLog.error("syncUserToSalesforce falhou ao definir contact key", undefined, { contactKey });
      return false;
    }

    // 2. Definir atributos básicos do cliente
    const attributes = buildUserAttributes(user);
    const attributesSet = await salesforceService.setAttributes(attributes);
    if (!attributesSet) {
      sfLog.warn("syncUserToSalesforce: contact key OK, mas atributos não foram todos aplicados", attributes);
    }

    // 3. Adicionar tags de segmentação
    const tags = buildUserTags(user);
    for (const tag of tags) {
      const tagOk = await salesforceService.addTag(tag);
      if (!tagOk) {
        sfLog.warn("syncUserToSalesforce: falha ao adicionar tag", { tag, contactKey });
      }
    }

    // 4. Registrar evento de login
    await salesforceService.trackEvent("login", {
      cpf_cnpj: user.cpfCnpj || "",
      tipo_cliente: user.company?.cnpj ? "PJ" : "PF",
      pv: contactKey,
    });

    sfLog.ok("syncUserToSalesforce concluído", {
      contactKey,
      atributos: Object.keys(attributes).length,
      tags: tags.length,
    });
    return true;
  } catch (error) {
    sfLog.error("syncUserToSalesforce erro inesperado", error, {
      clientId: user.clientId ?? "(ausente)",
    });
    return false;
  }
}

/**
 * Constrói os atributos do contato a partir do User
 */
function buildUserAttributes(user: User): Record<string, string | number | boolean> {
  const attributes: Record<string, string | number | boolean> = {};

  // Dados básicos
  if (user.cpfCnpj) {
    attributes.cpf_cnpj = user.cpfCnpj;
  }

  if (user.clientId) {
    attributes.pv = user.clientId;
  }

  // Dados do responsável
  if (user.responsible) {
    if (user.responsible.name) {
      attributes.nome = user.responsible.name;
    }
    if (user.responsible.email) {
      attributes.email = user.responsible.email;
    }
    if (user.responsible.cellphone) {
      attributes.telefone = user.responsible.cellphone;
    }
    if (user.responsible.birthday) {
      attributes.data_nascimento = user.responsible.birthday;
    }
  }

  // Dados da empresa (se PJ)
  if (user.company) {
    if (user.company.cnpj) {
      attributes.cnpj = user.company.cnpj;
    }
    if (user.company.name) {
      attributes.razao_social = user.company.name;
    }
    if (user.company.tradingName) {
      attributes.nome_fantasia = user.company.tradingName;
    }
    if (user.company.address?.city) {
      attributes.cidade = user.company.address.city;
    }
    if (user.company.address?.state) {
      attributes.estado = user.company.address.state;
    }
  }

  // Status e tipo
  attributes.tipo_cliente = user.company?.cnpj ? "PJ" : "PF";
  attributes.status = user.userStatus || "unknown";
  attributes.ativo = user.userStatus === "active";

  // Pontos
  if (user.isPointsEnabled !== undefined) {
    attributes.pontos_habilitado = user.isPointsEnabled;
  }
  if (user.balanceAvailablePoints !== undefined) {
    attributes.saldo_pontos = user.balanceAvailablePoints;
  }

  return attributes;
}

/**
 * Constrói as tags de segmentação a partir do User
 */
function buildUserTags(user: User): string[] {
  const tags: string[] = [];

  // Tag: Tipo de cliente
  if (user.company?.cnpj) {
    tags.push("tipo_pj");
  } else {
    tags.push("tipo_pf");
  }

  // Tag: Status
  if (user.userStatus === "active") {
    tags.push("cliente_ativo");
  } else {
    tags.push("cliente_inativo");
  }

  // Tag: Pontos habilitados
  if (user.isPointsEnabled) {
    tags.push("pontos_habilitado");
  }

  // Tag: Modo de atendimento
  if (user.logistic?.attendanceMode === "digital") {
    tags.push("atendimento_digital");
  } else if (user.logistic?.attendanceMode === "in-person") {
    tags.push("atendimento_presencial");
  }

  // Tag: Status de cadastro
  if (user.userStatus === "db_client" || user.userStatus === "db_client_created") {
    tags.push("cadastro_completo");
  } else {
    tags.push("cadastro_incompleto");
  }

  return tags;
}

/**
 * Remove os dados do usuário do Salesforce (ao fazer logout)
 */
export async function clearSalesforceUser(
  salesforceService: SalesforceMarketingCloudService
): Promise<void> {
  try {
    sfLog.info("Registrando logout no Salesforce");

    // Registrar evento de logout
    await salesforceService.trackEvent("logout");

    // Nota: Não removemos o contact key no logout, pois queremos
    // continuar enviando push notifications mesmo após logout
  } catch (error) {
    sfLog.error("Erro ao registrar logout no Salesforce", error);
  }
}

/**
 * Registra evento de compra
 */
export async function trackPurchase(
  salesforceService: SalesforceMarketingCloudService,
  purchaseData: {
    valorTotal: number;
    quantidadeItens: number;
    metodoPagamento?: string;
    produtos?: string[];
  }
): Promise<void> {
  try {
    await salesforceService.trackEvent("compra_realizada", {
      valor_total: purchaseData.valorTotal,
      quantidade_itens: purchaseData.quantidadeItens,
      metodo_pagamento: purchaseData.metodoPagamento || "nao_informado",
      produtos: purchaseData.produtos?.join(",") || "",
    });

    console.log("[Salesforce Helper] Compra registrada");
  } catch (error) {
    console.error("[Salesforce Helper] Erro ao registrar compra:", error);
  }
}

/**
 * Registra evento de produto visualizado
 */
export async function trackProductView(
  salesforceService: SalesforceMarketingCloudService,
  productData: {
    produtoId: string;
    nome?: string;
    categoria?: string;
    preco?: number;
  }
): Promise<void> {
  try {
    await salesforceService.trackEvent("produto_visualizado", {
      produto_id: productData.produtoId,
      nome: productData.nome || "",
      categoria: productData.categoria || "",
      preco: productData.preco || 0,
    });
  } catch (error) {
    console.error("[Salesforce Helper] Erro ao registrar visualização:", error);
  }
}

/**
 * Registra evento de carrinho abandonado
 */
export async function trackCartAbandoned(
  salesforceService: SalesforceMarketingCloudService,
  cartData: {
    valorTotal: number;
    quantidadeItens: number;
  }
): Promise<void> {
  try {
    await salesforceService.trackEvent("carrinho_abandonado", {
      valor_total: cartData.valorTotal,
      quantidade_itens: cartData.quantidadeItens,
    });

    console.log("[Salesforce Helper] Carrinho abandonado registrado");
  } catch (error) {
    console.error(
      "[Salesforce Helper] Erro ao registrar carrinho abandonado:",
      error
    );
  }
}
